import { Select, sizedSelectStyles } from "shared/components";
import { components, OptionProps, ValueContainerProps } from "react-select";
import { Checkbox } from "shared/components/forms/Checkbox";
import { ApplicantType } from "shared/types";
import { ReactNode } from "react";
import { DEFAULT_ROLES } from "../../constants";

const baseSelectStyles = sizedSelectStyles();

// FE only pseudo-role
const ALL_ROLES = "all_roles";
type SelectableRole = ApplicantType | typeof ALL_ROLES;

const ROLE_OPTIONS = [
  { value: ALL_ROLES, label: "All roles" },
  { value: ApplicantType.Applicant, label: "Applicants" },
  { value: ApplicantType.Occupant, label: "Occupants" },
  { value: ApplicantType.CoSigner, label: "Guarantors" }
];

const CheckboxOption = (props: OptionProps) => (
  <components.Option {...props} className="!flex items-center gap-2.5">
    <Checkbox
      checked={props.isSelected}
      onChange={() => null} // checkbox is visual only
      wrapperClassName="w-min"
    />
    <span className="font-medium my-1">{props.label}</span>
  </components.Option>
);

const SelectionDisplay = ({ children, ...props }: ValueContainerProps) => {
  const [selectedValues, focusAnchor] = children as ReactNode[];
  const allRolesSelected = props.getValue().length === ROLE_OPTIONS.length;
  return (
    <components.ValueContainer {...props}>
      {allRolesSelected ? <span>All roles</span> : selectedValues}
      {focusAnchor}
    </components.ValueContainer>
  );
};

export const RoleSelector = ({
  selectedRoles,
  setSelectedRoles
}: {
  selectedRoles: ApplicantType[];
  setSelectedRoles: (roles: ApplicantType[]) => void;
}) => {
  const displayedSelectedRoles: SelectableRole[] = DEFAULT_ROLES.every((role) =>
    selectedRoles.includes(role)
  )
    ? [ALL_ROLES, ...selectedRoles]
    : selectedRoles;

  const handleChange = (updatedRoles: SelectableRole[]) => {
    const hadAllRoles = displayedSelectedRoles.includes(ALL_ROLES);
    const selectingAllRoles = updatedRoles.includes(ALL_ROLES);

    if (selectingAllRoles && !hadAllRoles) setSelectedRoles(DEFAULT_ROLES);
    else if (!selectingAllRoles && hadAllRoles) setSelectedRoles([]);
    else
      setSelectedRoles(
        updatedRoles.filter((role) => role !== ALL_ROLES) as ApplicantType[]
      );
  };

  return (
    <Select
      id="applicant-type"
      required
      isMulti
      label="Who to include in calculation"
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      components={{ Option: CheckboxOption, ValueContainer: SelectionDisplay }}
      value={displayedSelectedRoles}
      onChange={(e) => {
        handleChange(
          (Array.isArray(e.target.value)
            ? e.target.value
            : [e.target.value]) as SelectableRole[]
        );
      }}
      styles={{
        control: (base, state) => ({
          ...baseSelectStyles.control?.(base, state),
          marginTop: "-8px"
        }),
        option: (base, state) => ({
          ...baseSelectStyles.option?.(base, state),
          backgroundColor: "#ffffff",
          ":active": {
            background: "#ffffff"
          }
        })
      }}
      options={ROLE_OPTIONS}
      clearable={false}
    />
  );
};
